/**
 * Página del Portal de Transparencia - Estilo Awwwards Interior
 * Información corporativa y cumplimiento normativo de INTERCASA, S.A.
 * Diseño elegante y funcional con animaciones sutiles
 */

import React, { useEffect, useRef } from 'react';
import LocalizedLink from '../components/common/LocalizedLink';
import { useTranslation, Trans } from 'react-i18next';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ImmersiveLayout from '../components/layout/ImmersiveLayout';
import { AnimatedSection } from '../components/animations';

gsap.registerPlugin(ScrollTrigger);

/**
 * Página del Portal de Transparencia con estilo Awwwards
 * @returns {JSX.Element} Página del Portal de Transparencia inmersiva
 */
const TransparencyPage = () => {
  const { t } = useTranslation('transparency');
  const heroRef = useRef(null);
  const cardsRef = useRef(null);

  // Animación del hero al cargar
  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;

    const tl = gsap.timeline();
    
    // Animación de entrada del hero
    tl.fromTo(hero.querySelector('.hero-title'),
      { y: 40, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.8, ease: 'power3.out' }
    )
    .fromTo(hero.querySelector('.hero-subtitle'),
      { y: 30, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.6, ease: 'power3.out' },
      '-=0.4'
    );

    return () => tl.kill();
  }, []);

  // Animaciones para las tarjetas de transparencia - por filas
  useEffect(() => {
    const cardsContainer = cardsRef.current;
    if (!cardsContainer) return;

    const cards = cardsContainer.querySelectorAll('.transparency-card');
    
    // Animar todas las cards con stagger, coordinado con el scroll
    gsap.fromTo(cards,
      { y: 40, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 0.5,
        stagger: {
          amount: 0.4, // Tiempo total para todas las cards
          from: 'start',
          grid: [3, 3], // Grid de 3 columnas
          axis: 'y', // Animar por filas
        },
        ease: 'power2.out',
        scrollTrigger: {
          trigger: cardsContainer,
          start: 'top 80%',
          toggleActions: 'play none none reverse',
        },
      }
    );

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  return (
    <ImmersiveLayout>
      {/* 
        =============================================
        1. HERO CON BANNER - Imagen de fondo
        =============================================
      */}
      <section 
        ref={heroRef}
        className="relative min-h-[60vh] flex items-center overflow-hidden"
      >
        {/* Fondo negro base */}
        <div className="absolute inset-0 bg-intercasa-dark" />
        
        {/* Imagen de fondo */}
        <div 
          className="absolute inset-0 opacity-70"
          style={{
            backgroundImage: 'url(/images/banners/transparencia-banner.webp)',
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
          }}
        />
        
        {/* Gradiente para mejor legibilidad */}
        <div className="absolute inset-0 bg-gradient-to-r from-intercasa-dark/90 via-intercasa-dark/70 to-intercasa-dark/90" />

        {/* Contenido del hero */}
        <div className="relative z-10 container mx-auto px-4 md:px-8 py-20">
          <div className="max-w-4xl mx-auto text-center">
            {/* Etiqueta superior */}
            <AnimatedSection animation="fadeUp" delay={0}>
              <span className="inline-block font-lato text-intercasa-yellow text-sm uppercase tracking-[0.2em] mb-6">
                {t('index.hero.label')}
              </span>
            </AnimatedSection>

            {/* Título */}
            <h1 className="hero-title font-baskerville font-bold text-white text-4xl md:text-5xl lg:text-6xl xl:text-7xl leading-[1.1] mb-6">
              <Trans i18nKey="index.hero.title" ns="transparency" components={{ hl: <span className="text-intercasa-yellow" /> }} />
            </h1>

            {/* Subtítulo */}
            <p className="hero-subtitle font-lato text-white/90 text-lg md:text-xl lg:text-2xl leading-relaxed max-w-3xl mx-auto text-center">
              {t('index.hero.subtitle')}
            </p>
          </div>
        </div>
      </section>
      {/* 
        =============================================
        2. INTRODUCCIÓN - Contexto legal
        =============================================
      */}
      <section className="bg-white py-24 md:py-32">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-4xl mx-auto">
            <AnimatedSection animation="fadeUp">
              <p className="font-lato text-lg text-intercasa-dark/70 leading-relaxed mb-6">
                <Trans i18nKey="index.intro.p1" ns="transparency" components={{ bold: <strong className="text-intercasa-dark" /> }} />
              </p>
            </AnimatedSection>
            
            <AnimatedSection animation="fadeUp" delay={0.1}>
              <p className="font-lato text-base text-intercasa-dark/70 leading-relaxed mb-6">
                {t('index.intro.p2')}
              </p>
            </AnimatedSection>

            <AnimatedSection animation="fadeUp" delay={0.15}>
              <p className="font-lato text-base text-intercasa-dark/70 leading-relaxed mb-6">
                {t('index.intro.p3')}
              </p>
            </AnimatedSection>

            <AnimatedSection animation="fadeUp" delay={0.2}>
              <p className="font-lato text-base text-intercasa-dark/70 leading-relaxed mb-6">
                {t('index.intro.p4')}
              </p>
            </AnimatedSection>

            <AnimatedSection animation="fadeUp" delay={0.25}>
              <p className="font-lato text-base text-intercasa-dark/70 leading-relaxed mb-8">
                <Trans i18nKey="index.intro.p5" ns="transparency" components={{ bold: <strong className="text-intercasa-dark" /> }} />
              </p>
            </AnimatedSection>

            <AnimatedSection animation="fadeUp" delay={0.3}>
              <p className="font-lato text-base text-intercasa-dark/70 leading-relaxed mb-8">
                <Trans i18nKey="index.intro.p6" ns="transparency" components={{ bold: <strong className="text-intercasa-dark" /> }} />
              </p>
            </AnimatedSection>
            
            <AnimatedSection animation="fadeUp" delay={0.35}>
              <div className="bg-intercasa-bone/50 border-l-4 border-intercasa-yellow p-6 rounded-r-lg mb-8">
                <p className="font-semibold text-intercasa-dark mb-3">{t('index.voluntary.label')}</p>
                <p className="text-intercasa-dark/70 text-base leading-relaxed">
                  <Trans i18nKey="index.voluntary.text" ns="transparency" components={{ bold: <strong className="text-intercasa-dark" /> }} />
                </p>
              </div>
            </AnimatedSection>

            {/* Botones de descarga */}
            <AnimatedSection animation="fadeUp" delay={0.4}>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-6 border-t border-intercasa-dark/10">
                <p className="font-lato text-intercasa-dark font-semibold text-sm mb-2 sm:mb-0">
                  {t('index.downloadIntro')}
                </p>
                <div className="flex gap-4">
                  {/* Botón PDF */}
                  <a
                    href="/PortalTansparenciaIntercasa/IntroduccionIntercasa/IntroduccionIntercasa.pdf"
                    download
                    className="group inline-flex items-center gap-2 px-6 py-3 bg-intercasa-red hover:bg-intercasa-red/90 text-white rounded-lg transition-all duration-300 hover:shadow-lg"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm5 6a1 1 0 10-2 0v3.586l-1.293-1.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V8z" clipRule="evenodd" />
                    </svg>
                    <span className="font-lato font-medium text-sm">PDF</span>
                  </a>

                  {/* Botón ODT */}
                  <a
                    href="/PortalTansparenciaIntercasa/IntroduccionIntercasa/IntroduccionIntercasa.odt"
                    download
                    className="group inline-flex items-center gap-2 px-6 py-3 bg-intercasa-dark hover:bg-intercasa-dark/90 text-white rounded-lg transition-all duration-300 hover:shadow-lg"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm5 6a1 1 0 10-2 0v3.586l-1.293-1.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V8z" clipRule="evenodd" />
                    </svg>
                    <span className="font-lato font-medium text-sm">ODT</span>
                  </a>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* 
        =============================================
        3. GRID DE SECCIONES DE TRANSPARENCIA
        =============================================
      */}
      <section className="bg-intercasa-bone py-24 md:py-32">
        <div className="container mx-auto px-4 md:px-8">
          {/* Header de sección */}
          <AnimatedSection animation="fadeUp" className="mb-16">
            <span className="font-lato text-intercasa-red text-sm uppercase tracking-widest mb-4 block">
              {t('index.sections.label')}
            </span>
            <h2 className="font-baskerville font-bold text-intercasa-dark text-3xl md:text-4xl lg:text-5xl">
              {t('index.sections.title')}
            </h2>
          </AnimatedSection>

          {/* Grid de tarjetas */}
          <div ref={cardsRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Información en Materia Organizativa */}
            <LocalizedLink
              to="/transparencia/normativa"
              className="transparency-card group relative bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 p-6 flex flex-col"
            >
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-intercasa-red/10 rounded-xl flex items-center justify-center group-hover:bg-intercasa-red group-hover:scale-110 transition-all duration-300">
                  <svg className="w-8 h-8 text-intercasa-red group-hover:text-white transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-4 0H9m-4 0H5m0 0v-5a2 2 0 012-2h2a2 2 0 012 2v5" />
                  </svg>
                </div>
              </div>
              <div className="flex-grow flex flex-col">
                <h3 className="font-baskerville text-xl font-bold text-intercasa-dark mb-2 text-center group-hover:text-intercasa-red transition-colors duration-300 min-h-[56px] flex items-center justify-center">
                  {t('index.cards.normativa')}
                </h3>
                <p className="text-sm text-intercasa-gray mb-4 text-center">
                  {t('index.updated')}
                </p>
              </div>
              <span className="flex items-center justify-center text-intercasa-red font-semibold text-sm group-hover:gap-3 gap-2 transition-all duration-300 mt-auto">
                {t('index.more')}
                <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </span>
              {/* Línea inferior animada */}
              <div className="absolute bottom-0 left-0 w-full h-1 bg-intercasa-red transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
            </LocalizedLink>

            {/* Información de Administradores y Directivos */}
            <LocalizedLink
              to="/transparencia/organizativa"
              className="transparency-card group relative bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 p-6 flex flex-col"
            >
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-intercasa-red/10 rounded-xl flex items-center justify-center group-hover:bg-intercasa-red group-hover:scale-110 transition-all duration-300">
                  <svg className="w-8 h-8 text-intercasa-red group-hover:text-white transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {/* Icono de persona con corbata/ejecutivo */}
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14c-4 0-6 2-6 4v1h12v-1c0-2-2-4-6-4z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l-1 3h2l-1-3z" />
                  </svg>
                </div>
              </div>
              <div className="flex-grow flex flex-col">
                <h3 className="font-baskerville text-xl font-bold text-intercasa-dark mb-2 text-center group-hover:text-intercasa-red transition-colors duration-300 min-h-[56px] flex items-center justify-center">
                  {t('index.cards.organizativa')}
                </h3>
                <p className="text-sm text-intercasa-gray mb-4 text-center">
                  {t('index.updated')}
                </p>
              </div>
              <span className="flex items-center justify-center text-intercasa-red font-semibold text-sm group-hover:gap-3 gap-2 transition-all duration-300 mt-auto">
                {t('index.more')}
                <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </span>
              <div className="absolute bottom-0 left-0 w-full h-1 bg-intercasa-red transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
            </LocalizedLink>

            {/* Servicios y Procedimientos */}
            <LocalizedLink
              to="/transparencia/servicios"
              className="transparency-card group relative bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 p-6 flex flex-col"
            >
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-intercasa-red/10 rounded-xl flex items-center justify-center group-hover:bg-intercasa-red group-hover:scale-110 transition-all duration-300">
                  <svg className="w-8 h-8 text-intercasa-red group-hover:text-white transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                </div>
              </div>
              <div className="flex-grow flex flex-col">
                <h3 className="font-baskerville text-xl font-bold text-intercasa-dark mb-2 text-center group-hover:text-intercasa-red transition-colors duration-300 min-h-[56px] flex items-center justify-center">
                  {t('index.cards.servicios')}
                </h3>
                <p className="text-sm text-intercasa-gray mb-4 text-center">
                  {t('index.updated')}
                </p>
              </div>
              <span className="flex items-center justify-center text-intercasa-red font-semibold text-sm group-hover:gap-3 gap-2 transition-all duration-300 mt-auto">
                {t('index.more')}
                <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </span>
              <div className="absolute bottom-0 left-0 w-full h-1 bg-intercasa-red transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
            </LocalizedLink>

            {/* Información Económico-Financiera */}
            <LocalizedLink
              to="/transparencia/economica"
              className="transparency-card group relative bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 p-6 flex flex-col"
            >
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-intercasa-red/10 rounded-xl flex items-center justify-center group-hover:bg-intercasa-red group-hover:scale-110 transition-all duration-300">
                  <svg className="w-8 h-8 text-intercasa-red group-hover:text-white transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
              </div>
              <div className="flex-grow flex flex-col">
                <h3 className="font-baskerville text-xl font-bold text-intercasa-dark mb-2 text-center group-hover:text-intercasa-red transition-colors duration-300 min-h-[56px] flex items-center justify-center">
                  {t('index.cards.economica')}
                </h3>
                <p className="text-sm text-intercasa-gray mb-4 text-center">
                  {t('index.updated')}
                </p>
              </div>
              <span className="flex items-center justify-center text-intercasa-red font-semibold text-sm group-hover:gap-3 gap-2 transition-all duration-300 mt-auto">
                {t('index.more')}
                <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </span>
              <div className="absolute bottom-0 left-0 w-full h-1 bg-intercasa-red transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
            </LocalizedLink>

            {/* Información de Contratos */}
            <LocalizedLink
              to="/transparencia/acceso"
              className="transparency-card group relative bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 p-6 flex flex-col"
            >
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-intercasa-red/10 rounded-xl flex items-center justify-center group-hover:bg-intercasa-red group-hover:scale-110 transition-all duration-300">
                  <svg className="w-8 h-8 text-intercasa-red group-hover:text-white transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <div className="flex-grow flex flex-col">
                <h3 className="font-baskerville text-xl font-bold text-intercasa-dark mb-2 text-center group-hover:text-intercasa-red transition-colors duration-300 min-h-[56px] flex items-center justify-center">
                  {t('index.cards.acceso')}
                </h3>
                <p className="text-sm text-intercasa-gray mb-4 text-center">
                  {t('index.updated')}
                </p>
              </div>
              <span className="flex items-center justify-center text-intercasa-red font-semibold text-sm group-hover:gap-3 gap-2 transition-all duration-300 mt-auto">
                {t('index.more')}
                <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </span>
              <div className="absolute bottom-0 left-0 w-full h-1 bg-intercasa-red transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
            </LocalizedLink>

            {/* Contratos y Convenios */}
            <LocalizedLink
              to="/transparencia/contratos"
              className="transparency-card group relative bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 p-6 flex flex-col"
            >
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-intercasa-red/10 rounded-xl flex items-center justify-center group-hover:bg-intercasa-red group-hover:scale-110 transition-all duration-300">
                  <svg className="w-8 h-8 text-intercasa-red group-hover:text-white transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
              </div>
              <div className="flex-grow flex flex-col">
                <h3 className="font-baskerville text-xl font-bold text-intercasa-dark mb-2 text-center group-hover:text-intercasa-red transition-colors duration-300 min-h-[56px] flex items-center justify-center">
                  {t('index.cards.contratos')}
                </h3>
                <p className="text-sm text-intercasa-gray mb-4 text-center">
                  {t('index.updated')}
                </p>
              </div>
              <span className="flex items-center justify-center text-intercasa-red font-semibold text-sm group-hover:gap-3 gap-2 transition-all duration-300 mt-auto">
                {t('index.more')}
                <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </span>
              <div className="absolute bottom-0 left-0 w-full h-1 bg-intercasa-red transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
            </LocalizedLink>

            {/* Subvenciones y Ayudas Públicas */}
            <LocalizedLink
              to="/transparencia/ayudas"
              className="transparency-card group relative bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 p-6 flex flex-col"
            >
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-intercasa-red/10 rounded-xl flex items-center justify-center group-hover:bg-intercasa-red group-hover:scale-110 transition-all duration-300">
                  <svg className="w-8 h-8 text-intercasa-red group-hover:text-white transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <div className="flex-grow flex flex-col">
                <h3 className="font-baskerville text-xl font-bold text-intercasa-dark mb-2 text-center group-hover:text-intercasa-red transition-colors duration-300 min-h-[56px] flex items-center justify-center">
                  {t('index.cards.ayudas')}
                </h3>
                <p className="text-sm text-intercasa-gray mb-4 text-center">
                  {t('index.updated')}
                </p>
              </div>
              <span className="flex items-center justify-center text-intercasa-red font-semibold text-sm group-hover:gap-3 gap-2 transition-all duration-300 mt-auto">
                {t('index.more')}
                <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </span>
              <div className="absolute bottom-0 left-0 w-full h-1 bg-intercasa-red transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
            </LocalizedLink>
          </div>
        </div>
      </section>

      {/* 
        =============================================
        4. CTA FINAL - CONTACTO
        =============================================
      */}
      <section className="bg-intercasa-dark py-24 md:py-32 relative overflow-hidden">
        {/* Elementos decorativos */}
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-intercasa-red/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-intercasa-red/10 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <AnimatedSection animation="fadeUp">
              <span className="font-lato text-intercasa-red text-sm uppercase tracking-widest mb-6 block">
                {t('index.cta.label')}
              </span>
              
              <h2 className="font-baskerville font-bold text-white text-3xl md:text-4xl lg:text-5xl mb-8 leading-tight">
                <Trans i18nKey="index.cta.title" ns="transparency" components={{ hl: <span className="text-intercasa-yellow" /> }} />
              </h2>
              
              <p className="font-lato text-lg text-white/60 leading-relaxed mb-12">
                {t('index.cta.text')}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href="mailto:notificaciones@grumasa.es"
                  className="group inline-flex items-center justify-center bg-white text-intercasa-dark font-semibold px-8 py-4 rounded-full hover:bg-intercasa-yellow transition-all duration-300"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  {t('index.cta.request')}
                  <svg className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </a>
                
                <a 
                  href="https://sede.registradores.org/sede/sede-corpme-web/home"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center justify-center bg-transparent border-2 border-white/30 text-white font-semibold px-8 py-4 rounded-full hover:border-white hover:bg-white/10 transition-all duration-300"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                  {t('index.cta.registry')}
                  <svg className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </a>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </ImmersiveLayout>
  );
};

export default TransparencyPage;